import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Swords, Trophy, Loader2, ArrowRight, Timer, Target, Zap, AlertTriangle } from "lucide-react";

/** Public duel page: shows challenger CTA if pending, or side-by-side result if completed. */
export default function DuelPage() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [d, setD] = useState(null);
  const [state, setState] = useState("loading"); // loading | ok | notfound
  const [accepting, setAccepting] = useState(false);

  const load = () => {
    api.get(`/duels/${code}`)
      .then((r) => { setD(r.data); setState("ok"); })
      .catch(() => setState("notfound"));
  };

  useEffect(() => {
    document.title = `Duel · ExamBro`;
    load();
    // eslint-disable-next-line
  }, [code]);

  const accept = async () => {
    if (!user) {
      toast.info("Sign in to accept the duel");
      navigate(`/login?next=/duel/${code}`);
      return;
    }
    setAccepting(true);
    try {
      const r = await api.post(`/duels/${code}/accept`);
      navigate(`/exam/${r.data.exam_id}?attempt=${r.data.attempt_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not accept duel");
      setAccepting(false);
    }
  };

  if (state === "loading" || authLoading) {
    return <div className="min-h-screen flex items-center justify-center text-stone-500 font-mono text-sm">Loading duel…</div>;
  }
  if (state === "notfound") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-stone-500">
        <div className="font-heading text-3xl font-bold text-stone-900 mb-2">Duel not found</div>
        <Link to="/" className="text-orange-600 mt-4 text-sm">← Back to ExamBro</Link>
      </div>
    );
  }

  const both = !!d.challenger;
  const winner = both
    ? (d.creator_score_pct >= d.challenger_score_pct ? "creator" : "challenger")
    : null;

  return (
    <div className="min-h-screen bg-stone-50">
      <div className="border-b border-stone-200 bg-white">
        <div className="max-w-4xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-heading text-lg font-bold">
            <div className="h-6 w-6 rounded bg-orange-600 flex items-center justify-center text-white text-xs font-bold">E</div>
            <span>ExamBro</span>
          </Link>
          <Badge className="bg-rose-600 hover:bg-rose-600 text-white font-mono text-[10px] uppercase tracking-[0.2em]">
            <Swords className="h-3 w-3 mr-1" /> Duel
          </Badge>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-10 space-y-6">
        {/* Header */}
        <div className="text-center">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-orange-600 mb-2">
            {both ? "Duel result" : "You've been challenged"}
          </div>
          <h1 className="font-heading text-4xl font-bold tracking-tight">{d.exam_title}</h1>
          <p className="text-stone-500 mt-2 font-mono text-sm">
            {d.question_count} questions · {d.exam_time_minutes}m
          </p>
        </div>

        {/* Side-by-side */}
        <div className="grid md:grid-cols-2 gap-4">
          <DuelistCard
            role="creator"
            person={d.creator}
            scorePct={d.creator_score_pct}
            metrics={both ? d.comparison?.creator : null}
            isWinner={winner === "creator"}
            highlight
            data-testid="duel-creator"
          />
          <DuelistCard
            role="challenger"
            person={d.challenger}
            scorePct={d.challenger_score_pct}
            metrics={both ? d.comparison?.challenger : null}
            isWinner={winner === "challenger"}
            pending={!both}
            data-testid="duel-challenger"
          />
        </div>

        {/* CTA if not accepted */}
        {!both && (
          <Card className="p-8 rounded-md border-orange-300 shadow-none bg-gradient-to-br from-orange-50 to-white text-center" data-testid="duel-cta">
            <Trophy className="h-10 w-10 mx-auto text-orange-600 mb-3" />
            <h2 className="font-heading text-2xl font-bold mb-2">Can you beat {d.creator?.name?.split(" ")[0] || "them"}?</h2>
            <p className="text-stone-600 text-sm mb-4">
              Take the exact same paper under the same conditions. Result auto-appears here — instantly shareable.
            </p>
            <Button
              onClick={accept}
              disabled={accepting}
              className="bg-orange-600 hover:bg-orange-700 text-white h-11 px-6"
              data-testid="duel-accept-btn"
            >
              {accepting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Swords className="h-4 w-4 mr-2" />}
              Accept the duel
            </Button>
            {!user && (
              <p className="text-xs text-stone-500 mt-3">Sign-in required. Your first duel is free.</p>
            )}
          </Card>
        )}

        {both && winner && (
          <Card className="p-6 rounded-md border-stone-200 shadow-none text-center" data-testid="duel-winner">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-stone-500 mb-1">Winner</div>
            <div className="font-heading text-3xl font-bold text-orange-600">
              {winner === "creator" ? d.creator?.name : d.challenger?.name}
            </div>
            <p className="text-sm text-stone-600 mt-2">
              {winner === "creator" ? d.creator_score_pct : d.challenger_score_pct}% vs {winner === "creator" ? d.challenger_score_pct : d.creator_score_pct}%
            </p>
          </Card>
        )}
      </div>
    </div>
  );
}

function DuelistCard({ role, person, scorePct, metrics, isWinner, pending, highlight, ...rest }) {
  return (
    <Card
      className={`p-5 rounded-md border shadow-none ${
        isWinner ? "border-orange-500 ring-2 ring-orange-500/30" : "border-stone-200"
      }`}
      {...rest}
    >
      <div className="flex items-center gap-3 mb-4">
        <div className="h-10 w-10 rounded-full bg-stone-900 flex items-center justify-center text-white text-sm font-heading font-bold flex-shrink-0">
          {person?.picture ? (
            <img src={person.picture} alt="" className="h-10 w-10 rounded-full object-cover" />
          ) : (
            (person?.name?.[0] || "?").toUpperCase()
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">
            {role === "creator" ? "Challenger" : "You"}
          </div>
          <div className="font-heading font-semibold truncate">
            {person?.name || (pending ? "Waiting for opponent…" : "—")}
          </div>
        </div>
        {isWinner && (
          <Trophy className="h-5 w-5 text-orange-600 flex-shrink-0" />
        )}
      </div>

      <div className="text-center py-3">
        <div className="font-heading text-5xl font-bold tabular-nums text-orange-600">
          {pending ? "?" : `${scorePct}%`}
        </div>
      </div>

      {metrics && (
        <div className="grid grid-cols-2 gap-3 mt-4 pt-4 border-t border-stone-200 text-xs">
          <MetricRow icon={Target} label="Accuracy" v={`${metrics.accuracy}%`} />
          <MetricRow icon={Timer} label="Time" v={`${Math.round((metrics.time_used_seconds || 0) / 60)}m`} />
          <MetricRow icon={Zap} label="Decision" v={Math.round(metrics.decision_quality || 0)} />
          <MetricRow icon={AlertTriangle} label="Pressure" v={Math.round(metrics.pressure_index || 0)} />
        </div>
      )}
    </Card>
  );
}

function MetricRow({ icon: Icon, label, v }) {
  return (
    <div className="flex items-center gap-2">
      <Icon className="h-3 w-3 text-stone-400" />
      <span className="font-mono text-stone-500">{label}:</span>
      <span className="font-mono font-semibold">{v}</span>
    </div>
  );
}
